/**
 * API client — local Vite proxies /api; production uses VITE_API_URL (GCP Cloud Run)
 */

export class ApiError extends Error {
  status: number
  body: unknown
  constructor(status: number, message: string, body?: unknown) {
    super(message)
    this.status = status
    this.body = body
  }
}

/** e.g. https://flowline-api-xxxxx.run.app — no trailing slash */
export function getApiBase(): string {
  const raw = (import.meta.env.VITE_API_URL as string | undefined)?.trim() || ''
  return raw.replace(/\/$/, '')
}

export function apiUrl(path: string): string {
  const p = path.startsWith('/') ? path : `/${path}`
  const base = getApiBase()
  return base ? `${base}${p}` : p
}

function authHeaders(): Record<string, string> {
  try {
    const token = localStorage.getItem('flowline_token')
    if (token) return { Authorization: `Bearer ${token}` }
  } catch {
    /* ignore */
  }
  return {}
}

export async function api<T>(
  path: string,
  init?: RequestInit & { signal?: AbortSignal }
): Promise<T> {
  const res = await fetch(apiUrl(path), {
    ...init,
    headers: {
      Accept: 'application/json',
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...authHeaders(),
      ...init?.headers
    }
  })
  const text = await res.text()
  let body: unknown = null
  try {
    body = text ? JSON.parse(text) : null
  } catch {
    body = text
  }
  if (!res.ok) {
    const msg =
      (body as { error?: { message?: string }; message?: string })?.error?.message ||
      (body as { error?: string })?.error ||
      (body as { message?: string })?.message ||
      `HTTP ${res.status}`
    throw new ApiError(res.status, String(msg), body)
  }
  return body as T
}
